export type BuilderStudioInlineEnterAction =
  | "hardBreak"
  | "insertBlock"
  | "itemBreak"
  | "native";

export type BuilderStudioSlashTextMatch = {
  deleteEnd: number;
  deleteStart: number;
  query: string;
  slashEnd: number;
  slashStart: number;
};

export function resolveBuilderStudioInlineEnterAction(options: {
  altKey?: boolean;
  ctrlKey?: boolean;
  itemBreak: boolean;
  metaKey?: boolean;
  multiline: boolean;
  splitBlock?: boolean;
  shiftKey?: boolean;
}): BuilderStudioInlineEnterAction {
  if (options.altKey || options.ctrlKey || options.metaKey) {
    return "native";
  }

  if (options.shiftKey) {
    return "hardBreak";
  }

  if (options.itemBreak) {
    return "itemBreak";
  }

  if (options.splitBlock) {
    return "insertBlock";
  }

  return options.multiline ? "hardBreak" : "insertBlock";
}

/**
 * Resolves the slash line that owns the current collapsed caret.
 *
 * Whitespace before "/" is accepted, but arbitrary text on the same line is
 * not. Removing the command also removes one adjacent line break so content
 * before and after it stays as two clean text fragments.
 */
export function builderStudioSlashTextMatch(
  fullText: string,
  caretOffset: number,
): BuilderStudioSlashTextMatch | null {
  const caret = Math.max(
    0,
    Math.min(fullText.length, Math.trunc(caretOffset)),
  );
  const beforeText = fullText.slice(0, caret);
  const lineStart = Math.max(
    beforeText.lastIndexOf("\n"),
    beforeText.lastIndexOf("\r"),
  ) + 1;
  const currentLineBeforeCaret = beforeText.slice(lineStart);
  const match = currentLineBeforeCaret.match(
    /^[\t \u00a0\u200b]*\/([^\n\r]*)$/,
  );

  if (!match) {
    return null;
  }

  const commandText = match[0].replace(
    /^[\t \u00a0\u200b]+/,
    "",
  );
  const slashStart = (
    lineStart + currentLineBeforeCaret.length - commandText.length
  );
  const nextLineBreakIndex = fullText
    .slice(caret)
    .search(/[\n\r]/);
  const lineEnd = nextLineBreakIndex >= 0
    ? caret + nextLineBreakIndex
    : fullText.length;
  const hasNextLineBreak = lineEnd < fullText.length;
  const hasPreviousLineBreak = lineStart > 0;
  const lineBreakEnd = (
    hasNextLineBreak
    && fullText[lineEnd] === "\r"
    && fullText[lineEnd + 1] === "\n"
  )
    ? lineEnd + 2
    : hasNextLineBreak
      ? lineEnd + 1
      : lineEnd;
  const lineBreakStart = (
    hasPreviousLineBreak
    && fullText[lineStart - 2] === "\r"
    && fullText[lineStart - 1] === "\n"
  )
    ? lineStart - 2
    : hasPreviousLineBreak
      ? lineStart - 1
      : lineStart;

  return {
    deleteEnd: hasNextLineBreak ? lineBreakEnd : lineEnd,
    deleteStart: hasNextLineBreak ? lineStart : lineBreakStart,
    query: commandText.slice(1),
    slashEnd: caret,
    slashStart,
  };
}

export function splitBuilderStudioSlashText(
  fullText: string,
  match: Pick<BuilderStudioSlashTextMatch, "deleteEnd" | "deleteStart">,
): {
  afterText: string;
  beforeText: string;
} {
  const start = Math.max(
    0,
    Math.min(fullText.length, Math.trunc(match.deleteStart)),
  );
  const end = Math.max(
    start,
    Math.min(fullText.length, Math.trunc(match.deleteEnd)),
  );

  return {
    afterText: fullText.slice(end),
    beforeText: fullText.slice(0, start),
  };
}

function builderStudioRichTextSlice(
  segments: readonly SiteBuilderRichTextSegment[],
  start: number,
  end: number,
): SiteBuilderRichTextSegment[] {
  const result: SiteBuilderRichTextSegment[] = [];
  let offset = 0;

  for (const segment of segments) {
    const segmentStart = offset;
    const segmentEnd = offset + segment.text.length;
    const sliceStart = Math.max(start, segmentStart);
    const sliceEnd = Math.min(end, segmentEnd);

    if (sliceStart < sliceEnd) {
      result.push({
        ...(segment.marks ? { marks: { ...segment.marks } } : {}),
        text: segment.text.slice(
          sliceStart - segmentStart,
          sliceEnd - segmentStart,
        ),
      });
    }

    offset = segmentEnd;
  }

  return result;
}

function compactBuilderStudioRichText(
  segments: readonly SiteBuilderRichTextSegment[],
): SiteBuilderRichTextSegment[] {
  const result: SiteBuilderRichTextSegment[] = [];

  for (const segment of segments) {
    if (segment.text === "") {
      continue;
    }

    const previous = result.at(-1);

    if (
      previous
      && JSON.stringify(previous.marks ?? {})
        === JSON.stringify(segment.marks ?? {})
    ) {
      previous.text += segment.text;
    } else {
      result.push({
        ...(segment.marks ? { marks: { ...segment.marks } } : {}),
        text: segment.text,
      });
    }
  }

  return result;
}

export function splitBuilderStudioRichTextSegments(
  segments: readonly SiteBuilderRichTextSegment[],
  match: Pick<BuilderStudioSlashTextMatch, "deleteEnd" | "deleteStart">,
): {
  after: SiteBuilderRichTextSegment[];
  before: SiteBuilderRichTextSegment[];
} {
  const textLength = segments.reduce(
    (length, segment) => length + segment.text.length,
    0,
  );
  const start = Math.max(
    0,
    Math.min(textLength, Math.trunc(match.deleteStart)),
  );
  const end = Math.max(
    start,
    Math.min(textLength, Math.trunc(match.deleteEnd)),
  );

  return {
    after: compactBuilderStudioRichText(
      builderStudioRichTextSlice(segments, end, textLength),
    ),
    before: compactBuilderStudioRichText(
      builderStudioRichTextSlice(segments, 0, start),
    ),
  };
}

export function normalizeBuilderStudioInlineText(
  value: string,
  preserveLineBreaks: boolean,
): string {
  const normalized = value
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .replace(/\r\n?/g, "\n");

  return preserveLineBreaks
    ? normalized.replace(/^[\t ]+|[\t ]+$/g, "")
    : normalized.trim().replace(/\s+/g, " ");
}

export function builderStudioCanInsertBlockTypes(
  currentCount: number,
  insertedTypes: readonly string[],
  options: {
    accepts?: readonly string[];
    capacity?: number;
    replaceCount?: number;
  } = {},
): boolean {
  if (
    options.accepts
    && insertedTypes.some((type) => !options.accepts?.includes(type))
  ) {
    return false;
  }

  const replaceCount = Math.min(
    Math.max(0, options.replaceCount ?? 0),
    Math.max(0, currentCount),
  );

  return typeof options.capacity !== "number"
    || (
      currentCount
      - replaceCount
      + insertedTypes.length
      <= options.capacity
    );
}
import type {
  SiteBuilderRichTextSegment,
} from "@webdo/site-templates/builder";
